import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase/config'; 
import { 
    collection, 
    doc, 
    getDoc,
    getDocs,
    runTransaction,
    serverTimestamp 
} from 'firebase/firestore';

import { 
    parseCurrency, 
    formatCurrencyForInput, 
    formatDateForInput,
    TIPOS_MOVIMIENTO_ART_MASTER
} from '../../utils/segurosUtils';

// Hook de Bitácoras Centralizado para el Timeline de Observaciones
import { useBitacoraTimeline } from '../../utils/useBitacoraTimeline';
import { useSegurosData } from '../../context/SegurosDataContext';
import { 
    cobranzaEstadosContratoArt, 
    cobranzaEstadosDeudaArt, 
    cobranzaEstadosGestionArt, 
    tiposMovimientoArt 
} from '../../data/listasDesplegables';

/**
 * ModalCobranzaART - Gestión de Cuenta Corriente, Saldos y Triple Dimensión de ART.
 * Optimizado para ediciones contables robustas basadas en deudas consolidadas (Target).
 */
const ModalCobranzaART = ({ show, onClose, onSuccess, contrato, clientData, initialData }) => {
    const { dbMasters } = useSegurosData();
    const [formData, setFormData] = useState({});
    const [newObservacion, setNewObservacion] = useState({ comentario: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fullClientData, setFullClientData] = useState(null);
    const [parentContractData, setParentContractData] = useState(null);
    const [uiError, setUiError] = useState(null);
    const [isLoadingFreshData, setIsLoadingFreshData] = useState(false);

    // Timeline del sub-historial de notas específicas
    const {
        items: observaciones,
        loading: loadingObservaciones,
        editingId: editingObservacionId,
        setEditingId: setEditingObservacionId,
        fetchNotes: fetchObservaciones,
        addLocalNote,
        updateLocalNote,
        removeLocalNote,
        persistBatchChanges
    } = useBitacoraTimeline('comentario');

    const selectOptionsMovimientos = useMemo(() => {
        const list = ['Pago Recibido', ...(tiposMovimientoArt || [])];
        return Array.from(new Set(list));
    }, []);

    const resolvedIds = useMemo(() => {
        let cId = initialData?.clienteId || contrato?.clienteId || clientData?.id;
        let coId = initialData?.contratoId || contrato?.id;

        if ((!cId || !coId) && initialData?.path) {
            const parts = initialData.path.split('/');
            const clientesIndex = parts.indexOf('clientes');
            const contratosIndex = parts.indexOf('contratos');
            if (clientesIndex !== -1 && clientesIndex + 1 < parts.length) {
                if (!cId) cId = parts[clientesIndex + 1];
            }
            if (contratosIndex !== -1 && contratosIndex + 1 < parts.length) {
                if (!coId) coId = parts[contratosIndex + 1];
            }
        }
        return { cId, coId };
    }, [initialData, contrato, clientData]);

    const observacionesPath = useMemo(() => {
        const { cId, coId } = resolvedIds;
        return (cId && coId && initialData?.id)
            ? `public/data/clientes/${cId}/contratos/${coId}/historial_cobranza/${initialData.id}/observaciones`
            : null;
    }, [resolvedIds, initialData?.id]);

    useEffect(() => {
        const fetchFreshData = async () => {
            const { cId, coId } = resolvedIds;
            if (!cId || !coId) return;
            setIsLoadingFreshData(true);
            try {
                const clientSnap = await getDoc(doc(db, 'public/data/clientes', cId));
                if (clientSnap.exists()) {
                    setFullClientData({ id: clientSnap.id, ...clientSnap.data() });
                }
                const contractSnap = await getDoc(doc(db, `public/data/clientes/${cId}/contratos`, coId));
                if (contractSnap.exists()) {
                    setParentContractData({ id: contractSnap.id, ...contractSnap.data() });
                }
            } catch (error) {
                console.error("Error al obtener datos frescos para Cobranza ART:", error);
                setUiError("No se pudieron cargar los saldos actuales del contrato. Intente de nuevo.");
            } finally {
                setIsLoadingFreshData(false);
            }
        };
        if (show) {
            setUiError(null);
            fetchFreshData();
        }
    }, [show, resolvedIds]);

    useEffect(() => {
        if (show) {
            if (initialData) {
                const esPagoMov = initialData.tipoMovimiento === 'Pago Recibido';
                // REGLA PROFESIONAL: Si editamos un ajuste, mostramos el saldoResultante (Target) en vez del importe (Delta)
                const valorInicialMostrar = esPagoMov ? initialData.importe : (initialData.saldoResultante ?? 0);

                setFormData({
                    ...initialData,
                    fechaMovimiento: formatDateForInput(initialData.fechaMovimiento || initialData.fechaCarga),
                    fechaReclamo: formatDateForInput(initialData.fechaReclamo),
                    fechaCobro: formatDateForInput(initialData.fechaCobro),
                    importe: formatCurrencyForInput(valorInicialMostrar),
                    tipoMovimiento: initialData.tipoMovimiento || '',
                    estadoContrato: initialData.estadoContrato || '',
                    estadoGestion: initialData.estadoGestion || '',
                    estadoDeuda: initialData.estadoDeuda || ''
                });
                if (observacionesPath) {
                    fetchObservaciones(observacionesPath);
                }
            } else {
                setFormData({
                    fechaMovimiento: new Date().toISOString().substring(0, 10),
                    importe: '',
                    tipoMovimiento: '', 
                    estadoContrato: '', 
                    estadoGestion: '',   
                    estadoDeuda: '',     
                    quienInformo: '',
                    fechaReclamo: '',
                    quienCobro: '',
                    fechaCobro: new Date().toISOString().substring(0, 10),
                });
                fetchObservaciones(null); 
            }
            setNewObservacion({ comentario: '' });
            setEditingObservacionId(null);
        }
    }, [initialData, show, observacionesPath, fetchObservaciones]);

    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    const saldoActualContrato = parentContractData?.artDeudaGlobal || 0;
    
    const calculosSimulacion = useMemo(() => {
        const valorIngresado = parseCurrency(formData.importe) || 0;
        const isAnuladoContrato = formData.estadoContrato === 'Anulado';
        
        if (isAnuladoContrato) {
            return { nuevoSaldo: 0, delta: -saldoActualContrato };
        }

        if (!formData.tipoMovimiento) {
            return { nuevoSaldo: saldoActualContrato, delta: 0 };
        }

        const esPago = formData.tipoMovimiento === 'Pago Recibido';

        if (esPago) {
            // Pago Recibido: Se ingresa el importe de pago neto, se resta del saldo de cartera actual
            const montoEfectivo = formData.estadoGestion === 'Anulada' ? 0 : valorIngresado;
            const nuevoSaldo = Math.max(0, saldoActualContrato - montoEfectivo);
            return { nuevoSaldo, delta: -montoEfectivo };
        } else {
            // Ajuste / Variación: El operador ingresa el saldo consolidado objetivo (Target)
            const targetDeuda = valorIngresado;
            
            // REGLA PROFESIONAL: Si es una edición, el delta se calcula contra el saldo anterior real de este hito,
            // de lo contrario, se calcula contra el saldo vivo del contrato.
            const saldoBaseDeComparacion = initialData ? (initialData.saldoAnterior ?? 0) : saldoActualContrato;
            const delta = targetDeuda - saldoBaseDeComparacion;
            
            return { nuevoSaldo: targetDeuda, delta: delta };
        }
    }, [saldoActualContrato, formData.importe, formData.tipoMovimiento, formData.estadoGestion, formData.estadoContrato, initialData]);

    const configVisual = TIPOS_MOVIMIENTO_ART_MASTER[formData.tipoMovimiento];
    const esPago = formData.tipoMovimiento === 'Pago Recibido';

    useEffect(() => {
        if (!show) return;

        const isPagoTotal = esPago && calculosSimulacion.nuevoSaldo === 0;

        if (isPagoTotal) {
            // Regla Pago Total: Todo se auto-completa y bloquea a su estado óptimo de cuenta saldada
            setFormData(prev => {
                if (prev.estadoContrato === 'Normalizado' && prev.estadoGestion === 'Cobrada' && prev.estadoDeuda === 'Al día') {
                    return prev;
                }
                return {
                    ...prev,
                    estadoContrato: 'Normalizado',
                    estadoGestion: 'Cobrada',
                    estadoDeuda: 'Al día'
                };
            });
        } else if (esPago && calculosSimulacion.nuevoSaldo > 0) {
            // Regla Pago Parcial: Sigue habiendo saldo deudor. El operador debe poder elegir los estados reales.
            setFormData(prev => {
                const updates = {};
                if (prev.estadoDeuda === 'Al día') {
                    updates.estadoDeuda = 'Con Deuda';
                }
                if (prev.estadoContrato === 'Normalizado' && prev.estadoGestion === 'Cobrada' && !prev.estadoContratoManualSet) {
                    updates.estadoContrato = parentContractData?.artEstadoContrato || '';
                    updates.estadoGestion = parentContractData?.artEstadoGestion || 'Pendiente';
                }
                if (Object.keys(updates).length > 0) {
                    return { ...prev, ...updates };
                }
                return prev;
            });
        }
    }, [calculosSimulacion.nuevoSaldo, esPago, show, parentContractData]);

    const handleImporteChange = (e) => {
        let value = e.target.value.replace(/\D/g, ''); 
        if (value) {
            const number = Number(value) / 100;
            setFormData(prev => ({ ...prev, importe: formatCurrencyForInput(number) }));
        } else { 
            setFormData(prev => ({ ...prev, importe: '' })); 
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const newState = { 
                ...prev, 
                [type === 'checkbox' ? name : name]: type === 'checkbox' ? checked : value,
                ...(name === 'estadoContrato' ? { estadoContratoManualSet: true } : {}),
                ...(name === 'estadoGestion' ? { estadoGestionManualSet: true } : {})
            };
            
            if (name === 'tipoMovimiento') {
                if (value === 'Pago Recibido') {
                    newState.quienInformo = '';
                    newState.fechaReclamo = '';
                } else {
                    newState.quienCobro = '';
                    newState.fechaCobro = '';
                }
            }
            return newState;
        });
    };

    const handleAddOrUpdateObservacion = () => {
        if (!newObservacion.comentario.trim()) return;

        if (editingObservacionId) {
            updateLocalNote(editingObservacionId, null, newObservacion.comentario);
            setEditingObservacionId(null);
        } else {
            addLocalNote(null, newObservacion.comentario);
        }
        setNewObservacion({ comentario: '' }); 
    };

    const handleEditObservacion = (obs) => {
        setEditingObservacionId(obs.id);
        setNewObservacion({ comentario: obs.comentario || '' });
    };

    const handleRemoveObservacion = (idToDelete) => {
        if (observacionesPath) {
            removeLocalNote(idToDelete, observacionesPath);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        setUiError(null);

        // Precomputamos el estado de deuda real para validar antes de persistir
        const simulatedDeudaStatus = calculosSimulacion.nuevoSaldo === 0 ? 'Al día' : (formData.estadoDeuda || 'Con Deuda');

        if (!formData.tipoMovimiento) {
            setUiError("Debe seleccionar un Tipo de Movimiento para continuar.");
            return;
        }
        if (!formData.estadoContrato) {
            setUiError("Debe seleccionar un Estado del contrato.");
            return;
        }
        if (!formData.estadoGestion) {
            setUiError("Debe seleccionar un Estado de Gestión de Deuda.");
            return;
        }

        setIsSubmitting(true);
        try {
            const { cId, coId } = resolvedIds;
            if (!cId || !coId) throw new Error("Faltan identificadores de Cliente o Contrato.");

            const valorIngresado = parseCurrency(formData.importe);
            if (valorIngresado <= 0) {
                setUiError("El importe ingresado debe ser mayor a cero.");
                setIsSubmitting(false);
                return;
            }

            // Para ajustes, la inyección neta en el libro diario es la diferencia absoluta (Delta)
            const deltaNeto = calculosSimulacion.delta;
            const montoIngresado = esPago ? valorIngresado : Math.abs(deltaNeto);

            if (!esPago && Math.abs(deltaNeto) <= 0.01) {
                setUiError("El saldo ingresado es igual al saldo previo de la cuenta. No hay diferencias netas para registrar.");
                setIsSubmitting(false);
                return;
            }

            const contractRef = doc(db, `public/data/clientes/${cId}/contratos`, coId);
            const historialCollRef = collection(db, `public/data/clientes/${cId}/contratos/${coId}/historial_cobranza`);
            
            const movimientoRef = initialData?.id 
                ? doc(db, `public/data/clientes/${cId}/contratos/${coId}/historial_cobranza`, initialData.id)
                : doc(historialCollRef); 

            // Consultamos todos los movimientos existentes en la base de datos para auditar el historial real
            const querySnapshot = await getDocs(historialCollRef);
            const docsList = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            // Determinamos el tipo de hito de ajuste real según la dirección del delta y el historial de la base de datos
            let tipoMovimientoFinal = formData.tipoMovimiento;
            if (!esPago) {
                if (deltaNeto > 0) {
                    // CRITERIO PROFESIONAL DE AUDITORÍA:
                    // Verificamos si existen otros movimientos contables en el historial (excluyendo el que estamos editando)
                    const tieneMovimientosPrevios = docsList.some(m => m.id !== movimientoRef.id);
                    // Solo es "Saldo Inicial" si es la primera transacción histórica absoluta del contrato
                    tipoMovimientoFinal = tieneMovimientosPrevios ? 'Aumento de Deuda' : 'Saldo Inicial';
                } else {
                    tipoMovimientoFinal = 'Disminución de Deuda';
                }
            }

            // Payload del movimiento contable
            const payloadMovimiento = {
                id: movimientoRef.id,
                tipoMovimiento: tipoMovimientoFinal,
                fechaMovimiento: formData.fechaMovimiento,
                fechaCarga: initialData?.fechaCarga || new Date(), 
                comentario: esPago 
                    ? `Pago Recibido de $ ${montoIngresado.toLocaleString('es-AR')}`
                    : `[AJUSTE AUTOMÁTICO] Ajuste contable para equiparar saldo de la compañía. Target: $ ${valorIngresado.toLocaleString('es-AR')} • Variación aplicada: ${deltaNeto >= 0 ? '+' : '-'} $ ${montoIngresado.toLocaleString('es-AR')}`,
                estadoContrato: formData.estadoContrato,
                estadoGestion: formData.estadoGestion,
                estadoDeuda: simulatedDeudaStatus,
                importe: montoIngresado,
                
                ...(esPago ? {
                    quienCobro: formData.quienCobro,
                    fechaCobro: formData.fechaCobro,
                } : {
                    quienInformo: formData.quienInformo || '',
                    fechaReclamo: formData.fechaReclamo || '',
                }),
            };

            let updatedList = [...docsList];
            const idxExistente = updatedList.findIndex(m => m.id === movimientoRef.id);
            if (idxExistente !== -1) {
                updatedList[idxExistente] = { ...updatedList[idxExistente], ...payloadMovimiento };
            } else {
                updatedList.push(payloadMovimiento);
            }

            // Orden cronológico estricto
            updatedList.sort((a, b) => {
                const dateA = a.fechaMovimiento || '';
                const dateB = b.fechaMovimiento || '';
                if (dateA !== dateB) return dateA.localeCompare(dateB);

                const timeA = a.fechaCarga?.seconds || (a.fechaCarga instanceof Date ? a.fechaCarga.getTime() / 1000 : Date.now() / 1000);
                const timeB = b.fechaCarga?.seconds || (b.fechaCarga instanceof Date ? b.fechaCarga.getTime() / 1000 : Date.now() / 1000);
                return timeA - timeB;
            });

            let runningBalance = 0;
            const updatesToSave = [];

            for (const mov of updatedList) {
                const config = TIPOS_MOVIMIENTO_ART_MASTER[mov.tipoMovimiento];
                const isAnulada = mov.estadoGestion === 'Anulada';
                const actualImporte = isAnulada ? 0 : (mov.importe || 0);

                const saldoAnterior = runningBalance;
                if (config) {
                    if (config.efecto === 'SUMA') {
                        runningBalance = runningBalance + actualImporte;
                    } else if (config.efecto === 'RESTA') {
                        runningBalance = Math.max(0, runningBalance - actualImporte);
                    }
                }

                // REGLA DE NEGOCIO: Si el contrato se anula, la deuda cae a 0
                if (mov.estadoContrato === 'Anulado') {
                    runningBalance = 0;
                }

                const saldoResultante = runningBalance;

                mov.saldoAnterior = saldoAnterior;
                mov.saldoResultante = saldoResultante;
                mov.estadoDeuda = runningBalance > 0 ? (mov.estadoDeuda === 'Incobrable' ? 'Incobrable' : 'Con Deuda') : 'Al día';

                updatesToSave.push({
                    ref: doc(db, `public/data/clientes/${cId}/contratos/${coId}/historial_cobranza`, mov.id),
                    data: {
                        ...mov,
                        saldoAnterior,
                        saldoResultante,
                        fechaCarga: mov.fechaCarga instanceof Date ? mov.fechaCarga : (mov.fechaCarga?.seconds ? new Date(mov.fechaCarga.seconds * 1000) : new Date())
                    }
                });
            }

            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(contractRef);
                if (!sfDoc.exists()) {
                    throw new Error("El contrato principal no existe.");
                }

                // Guardar la cadena de movimientos en Firestore
                for (const update of updatesToSave) {
                    transaction.set(update.ref, update.data, { merge: true });
                }

                const latestMov = [...updatedList].sort((a, b) => {
                    const dateA = a.fechaMovimiento || '';
                    const dateB = b.fechaMovimiento || '';
                    if (dateA !== dateB) return dateB.localeCompare(dateA);
                    
                    const timeA = a.fechaCarga?.seconds || (a.fechaCarga instanceof Date ? a.fechaCarga.getTime() / 1000 : Date.now() / 1000);
                    const timeB = b.fechaCarga?.seconds || (b.fechaCarga instanceof Date ? b.fechaCarga.getTime() / 1000 : Date.now() / 1000);
                    return timeB - timeA;
                })[0];

                const finalContractStatus = latestMov?.estadoContrato || formData.estadoContrato;
                const finalDeudaStatus = runningBalance > 0 ? (latestMov?.estadoDeuda === 'Incobrable' ? 'Incobrable' : 'Con Deuda') : 'Al día';

                transaction.update(contractRef, {
                    artDeudaGlobal: runningBalance,
                    tieneDeuda: runningBalance > 0, 
                    artEstadoContrato: finalContractStatus,
                    artEstadoGestion: latestMov?.estadoGestion || formData.estadoGestion || 'Pendiente',
                    artEstadoDeuda: finalDeudaStatus,
                    artUltimoMovimientoTipo: latestMov?.tipoMovimiento || tipoMovimientoFinal || '',
                    artUltimoMovimientoMonto: latestMov?.importe || montoIngresado || 0,
                    artUltimoMovimientoFecha: latestMov?.fechaMovimiento || '',
                    artUltimoReclamoFecha: latestMov?.fechaReclamo || '',
                    artUltimoMovimientoResponsable: latestMov?.tipoMovimiento === 'Pago Recibido' ? (latestMov?.quienCobro || '') : (latestMov?.quienInformo || ''),
                    artUltimaActualizacion: serverTimestamp()
                });
            });

            const finalObsPath = `public/data/clientes/${cId}/contratos/${coId}/historial_cobranza/${movimientoRef.id}/observaciones`;
            if (newObservacion.comentario.trim()) {
                addLocalNote(null, newObservacion.comentario.trim());
            }

            await persistBatchChanges(finalObsPath);
            
            onSuccess?.(initialData ? 'Movimiento contable actualizado.' : 'Balance de ART actualizado exitosamente.');
            onClose();
        } catch (error) { 
            console.error("Error crítico en transacción de Cuenta Corriente ART:", error); 
            setUiError("No se pudo procesar la transacción financiera. Por favor intente nuevamente.");
        } finally { 
            setIsSubmitting(false); 
        }
    };

    if (!show) return null;

    const esPagoTotalRealizado = esPago && calculosSimulacion.nuevoSaldo === 0;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-auto max-h-[95vh] flex flex-col overflow-hidden border border-gray-100 transition-all duration-300">
                
                {/* HEADER */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-slate-50 shrink-0 select-none">
                    <div className="flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-600 p-2.5 rounded-xl shrink-0">
                            <svg className="w-5 h-5" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                        </span>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 leading-none">
                                {initialData ? 'Editar Movimiento de Cuenta Corriente' : 'Actualizar Cuenta Corriente ART'}
                            </h3>
                            <p className="text-[10px] text-blue-800/80 font-black mt-1.5 tracking-wider uppercase">
                                Contrato: {parentContractData?.nContrato || contrato?.nContrato} • {parentContractData?.compania || contrato?.compania}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-slate-100 rounded-full">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* CUERPO DEL MODAL */}
                <div className="overflow-y-auto p-8 flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                    
                    {uiError && (
                        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 flex items-start gap-3 animate-fade-in">
                            <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div className="text-xs font-semibold">{uiError}</div>
                        </div>
                    )}

                    <div className="mb-6 p-5 rounded-2xl bg-gradient-to-r from-slate-800 to-slate-900 text-white shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        {isLoadingFreshData ? (
                            <div className="space-y-2 animate-pulse w-full max-w-[200px]">
                                <div className="h-3 bg-slate-700 rounded-full w-24"></div>
                                <div className="h-8 bg-slate-700 rounded-full w-44"></div>
                            </div>
                        ) : (
                            <div>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Saldo Vivo Previo en Cartera</span>
                                <h2 className="text-3xl font-black font-mono tracking-tight mt-0.5">
                                    $ {saldoActualContrato.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                </h2>
                            </div>
                        )}
                        
                        {isLoadingFreshData ? (
                            <div className="h-[52px] bg-slate-700 animate-pulse rounded-xl w-full max-w-[320px]"></div>
                        ) : (
                            <div className="flex items-center gap-4 bg-white/10 px-4 py-2.5 rounded-xl border border-white/10 select-none min-h-[64px]">
                                <div className="text-right">
                                    <span className="block text-[9px] text-slate-300 font-bold uppercase tracking-widest">
                                        Simulación Nuevo Saldo
                                    </span>
                                    <span className="font-mono text-lg font-black text-emerald-400">
                                        $ {calculosSimulacion.nuevoSaldo.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <div className="border-l border-white/10 pl-3">
                                    <span className="block text-[9px] text-slate-300 font-bold uppercase tracking-widest">Ajuste Neto (Delta)</span>
                                    <span className={`font-mono text-xs font-black ${calculosSimulacion.delta >= 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                                        {calculosSimulacion.delta >= 0 ? '+' : '-'} $ {Math.abs(calculosSimulacion.delta).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* FORMULARIO */}
                    <form id="cobranzaARTForm" onSubmit={handleSubmit} className="space-y-8">
                        
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                            <legend className="px-3 text-sm font-black text-slate-700 bg-white absolute -top-3 left-4 tracking-wider uppercase">Acción de Cuenta Corriente</legend>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                <div>
                                    <label className="block text-[13px] font-bold text-slate-700 mb-1.5 pl-1">Tipo de Movimiento *</label>
                                    <select 
                                        name="tipoMovimiento" 
                                        value={formData.tipoMovimiento || ''} 
                                        onChange={handleChange} 
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                        disabled={!!initialData}
                                        required
                                    >
                                        <option value="">Seleccione Movimiento...</option>
                                        {selectOptionsMovimientos.map(movName => (
                                            <option key={movName} value={movName}>
                                                {TIPOS_MOVIMIENTO_ART_MASTER[movName]?.label || movName.toUpperCase()}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[13px] font-bold text-slate-700 mb-1.5 pl-1">Fecha de Operación *</label>
                                    <input 
                                        type="date" 
                                        name="fechaMovimiento" 
                                        value={formData.fechaMovimiento || ''} 
                                        onChange={handleChange} 
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" 
                                        required 
                                    />
                                </div>

                                <div>
                                    <label className="block text-[13px] font-bold text-slate-700 mb-1.5 pl-1 font-sans">
                                        {esPago ? "Importe de Pago *" : "Deuda Total de Compañía (Objetivo) *"}
                                    </label>
                                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] bg-white focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 transition-all shadow-sm">
                                        <div className="bg-slate-50 px-4 h-full flex items-center border-r border-slate-100 shrink-0 select-none">
                                            <span className="font-bold text-slate-500 text-xs">$ ARS</span>
                                        </div>
                                        <input 
                                            type="text" 
                                            name="importe" 
                                            value={formData.importe || ''} 
                                            onChange={handleImporteChange} 
                                            className="w-full px-4 outline-none font-mono font-bold text-slate-800 text-lg h-full" 
                                            placeholder="0,00" 
                                            required 
                                        />
                                    </div>
                                    <span className="text-[10px] text-slate-400 pl-1 mt-1 block font-sans leading-relaxed">
                                        {esPago 
                                            ? 'Ingrese el monto neto cobrado al asegurado.' 
                                            : 'Ingrese la Deuda Total informada por la compañía. El sistema inyectará la variación neta de forma automática.'}
                                    </span>
                                </div>
                            </div>
                        </fieldset>

                        {/* TRIPLE DIMENSIÓN DE ESTADOS */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                            <legend className="px-3 text-sm font-black text-slate-700 bg-white absolute -top-3 left-4 tracking-wider uppercase">Estatus y Triple Dimension del Contrato</legend>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                <div>
                                    <label className="block text-[13px] font-bold text-slate-700 mb-1.5 pl-1">Estado del contrato</label>
                                    {isLoadingFreshData ? (
                                        <div className="h-[46px] bg-slate-100 animate-pulse rounded-xl w-full"></div>
                                    ) : (
                                        <select 
                                            name="estadoContrato" 
                                            value={formData.estadoContrato || ''} 
                                            onChange={handleChange} 
                                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                            required
                                            disabled={esPagoTotalRealizado}
                                        >
                                            <option value="">Seleccione Estado...</option>
                                            {cobranzaEstadosContratoArt?.map(c => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    )}
                                    {esPagoTotalRealizado && <p className="text-[10px] text-emerald-600 mt-1 pl-1 font-bold">🔒 Forzado a "Normalizado" (Pago Total).</p>}
                                    {esPago && !esPagoTotalRealizado && <p className="text-[10px] text-amber-600 mt-1 pl-1 font-bold">⚠️ Libre: Pago Parcial registrado.</p>}
                                </div>

                                <div>
                                    <label className="block text-[13px] font-bold text-slate-700 mb-1.5 pl-1">Estado de Gestión de Deuda</label>
                                    {isLoadingFreshData ? (
                                        <div className="h-[46px] bg-slate-100 animate-pulse rounded-xl w-full"></div>
                                    ) : (
                                        <select 
                                            name="estadoGestion" 
                                            value={formData.estadoGestion || ''} 
                                            onChange={handleChange} 
                                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                            required
                                            disabled={esPagoTotalRealizado}
                                        >
                                            <option value="">Seleccione Gestión...</option>
                                            {cobranzaEstadosGestionArt?.map(g => (
                                                <option key={g} value={g}>{g}</option>
                                            ))}
                                        </select>
                                    )}
                                    {esPagoTotalRealizado && <p className="text-[10px] text-emerald-600 mt-1 pl-1 font-bold">🔒 Forzado a "Cobrada" (Pago Total).</p>}
                                    {esPago && !esPagoTotalRealizado && <p className="text-[10px] text-amber-600 mt-1 pl-1 font-bold">⚠️ Libre: Asignar estatus de cobro actual.</p>}
                                </div>

                                <div>
                                    <label className="block text-[13px] font-bold text-slate-700 mb-1.5 pl-1">Estado de la Deuda</label>
                                    {isLoadingFreshData ? (
                                        <div className="h-[46px] bg-slate-100 animate-pulse rounded-xl w-full"></div>
                                    ) : calculosSimulacion.nuevoSaldo === 0 ? (
                                        <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 text-emerald-800 rounded-xl border border-emerald-100 h-[46px] select-none">
                                            <span className="text-xs font-bold flex items-center gap-1.5">
                                                <svg 
                                                    className="text-emerald-600 shrink-0" 
                                                    width="20" 
                                                    height="20" 
                                                    fill="currentColor" 
                                                    viewBox="0 0 20 20"
                                                    style={{ width: '20px', height: '20px', minWidth: '20px' }}
                                                >
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l5-5z" clipRule="evenodd"/>
                                                </svg>
                                                🔒 Automático: Al día
                                            </span>
                                        </div>
                                    ) : (
                                        <label className="flex items-center gap-3 cursor-pointer px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl hover:border-red-300 hover:bg-red-50/20 transition-all select-none h-[46px]">
                                            <input 
                                                type="checkbox"
                                                checked={formData.estadoDeuda === 'Incobrable'}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        estadoDeuda: isChecked ? 'Incobrable' : 'Con Deuda'
                                                    }));
                                                }}
                                                className="w-5 h-5 text-red-600 border-slate-300 rounded focus:ring-red-500 cursor-pointer accent-red-600 shrink-0"
                                            />
                                            <div className="text-left leading-tight">
                                                <span className="block text-xs font-bold text-red-600">Marcar Incobrable</span>
                                                <span className="block text-[9px] text-slate-400">Desestimar cobro comercial</span>
                                            </div>
                                        </label>
                                    )}
                                </div>
                            </div>
                        </fieldset>

                        {/* REGISTRO DE COBRO / ORIGEN DE NOVEDAD */}
                        {esPago ? (
                            <fieldset className="border border-emerald-200 rounded-xl p-6 relative bg-emerald-50/10 space-y-6 shadow-sm animate-fade-in">
                                <legend className="px-3 text-sm font-black text-emerald-700 bg-white absolute -top-3 left-4 tracking-wider uppercase">Registro de Cobro Efectuado</legend>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                    <div>
                                        <label className="block text-[13px] font-bold text-emerald-800 mb-1.5 pl-1">¿Quién Procesó el Cobro? *</label>
                                        <select 
                                            name="quienCobro" 
                                            value={formData.quienCobro || ''} 
                                            onChange={handleChange} 
                                            className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all" 
                                            required={esPago}
                                        >
                                            <option value="">Seleccione Colaborador...</option>
                                            {sortedColaboradores.map(v => (
                                                <option key={v.id} value={v.nombre}>{v.nombre}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-bold text-emerald-800 mb-1.5 pl-1">Fecha Efectiva de Pago *</label>
                                        <input 
                                            type="date" 
                                            name="fechaCobro" 
                                            value={formData.fechaCobro || ''} 
                                            onChange={handleChange} 
                                            className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all" 
                                            required={esPago} 
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        ) : formData.tipoMovimiento ? (
                            <fieldset className="border border-amber-200 rounded-xl p-6 relative bg-amber-50/10 space-y-6 shadow-sm animate-fade-in">
                                <legend className="px-3 text-sm font-black text-amber-700 bg-white absolute -top-3 left-4 tracking-wider uppercase">Origen de Modificación de Deuda</legend>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                    <div>
                                        <label className="block text-[13px] font-bold text-amber-900 mb-1.5 pl-1">¿Quién Aplicó / Informó la Novedad?</label>
                                        <select 
                                            name="quienInformo" 
                                            value={formData.quienInformo || ''} 
                                            onChange={handleChange} 
                                            className="w-full border border-amber-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-amber-400 focus:ring-1 focus:ring-amber-100 transition-all"
                                        >
                                            <option value="">Seleccione Colaborador/Cia...</option>
                                            {sortedColaboradores.map(v => (
                                                <option key={v.id} value={v.nombre}>{v.nombre}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-bold text-amber-900 mb-1.5 pl-1">Fecha de Notificación o Reclamo</label>
                                        <input 
                                            type="date" 
                                            name="fechaReclamo" 
                                            value={formData.fechaReclamo || ''} 
                                            onChange={handleChange} 
                                            className="w-full border border-amber-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-amber-400 focus:ring-1 focus:ring-amber-100 transition-all" 
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        ) : null}

                        {/* COMENTARIOS */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                            <legend className="px-3 text-sm font-black text-slate-700 bg-white absolute -top-3 left-4 tracking-wider uppercase">Comentarios y Justificaciones</legend>
                            
                            <div className={`p-4 border rounded-xl bg-blue-50/30 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end shadow-sm ${
                                editingObservacionId ? 'border-orange-400 bg-orange-50/10' : 'border-blue-100'
                            }`}>
                                <div className="md:col-span-3 text-left">
                                    <div className="flex items-center justify-between border-b border-blue-50 pb-2 mb-2 select-none">
                                        <h4 className="text-xs font-black text-blue-800 uppercase tracking-widest">
                                            {editingObservacionId ? '✏️ Editando Nota' : 'Nota de Bitácora del Movimiento'}
                                        </h4>
                                    </div>
                                    <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-widest pl-1 mb-1 select-none">Comentario</label>
                                    <input 
                                        type="text" 
                                        name="comentario" 
                                        value={newObservacion.comentario} 
                                        onChange={(e) => setNewObservacion({...newObservacion, comentario: e.target.value})} 
                                        className="w-full border border-slate-200 rounded-lg px-3 text-xs text-slate-800 outline-none bg-white h-9 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" 
                                        placeholder="Motivo del ajuste, número de comprobante o acuerdo..." 
                                    />
                                </div>
                                <button 
                                    type="button" 
                                    onClick={handleAddOrUpdateObservacion} 
                                    className={`font-bold h-9 px-4 rounded-lg text-xs uppercase tracking-widest text-white shadow-md active:scale-95 transition-all w-full ${
                                        editingObservacionId ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-100' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
                                    }`}
                                >
                                    {editingObservacionId ? 'Actualizar' : 'Agregar'}
                                </button>
                            </div>

                            <div className="max-h-60 overflow-y-auto space-y-3 px-1 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                                {loadingObservaciones ? (
                                    <p className="text-center text-slate-400 italic text-sm py-4 animate-pulse uppercase tracking-wider font-bold">Cargando notas...</p>
                                ) : observaciones.length === 0 ? (
                                    <p className="text-center text-slate-400 italic text-xs py-4 select-none">Sin anotaciones específicas registradas para este movimiento</p>
                                ) : (
                                    observaciones.map((o) => (
                                        <div key={o.id} className={`p-4 rounded-xl border bg-white shadow-sm flex flex-col gap-2 transition-all duration-300 animate-fade-in text-left ${
                                            editingObservacionId === o.id ? 'border-orange-400 bg-orange-50/50 ring-2 ring-orange-100' : 'border-slate-100 hover:border-blue-200'
                                        }`}>
                                            <div className="flex justify-between items-center select-none">
                                                <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest tabular-nums">
                                                    {o.fechaCreacion?.seconds 
                                                        ? new Date(o.fechaCreacion.seconds * 1000).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) 
                                                        : (o.fechaCreacion instanceof Date ? o.fechaCreacion.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : new Date().toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }))}
                                                </span>
                                                <div className="flex gap-3">
                                                    <button type="button" onClick={() => handleEditObservacion(o)} className="text-blue-400 text-[10px] uppercase font-black hover:text-blue-600 transition-colors">Editar</button>
                                                    <button type="button" onClick={() => handleRemoveObservacion(o.id)} className="text-red-300 text-[10px] uppercase font-black hover:text-red-500 transition-colors">Eliminar</button>
                                                </div>
                                            </div>
                                            <p className="text-sm text-slate-600 leading-relaxed italic pr-4">{o.comentario}</p>
                                        </div>
                                    ))
                                )}
                            </div>
                        </fieldset>
                    </form>
                </div>

                {/* BOTONERA INFERIOR */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 shrink-0 select-none">
                    <button 
                        onClick={onClose} 
                        className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 shadow-sm transition-all transform active:scale-95"
                    >
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        form="cobranzaARTForm" 
                        disabled={isSubmitting} 
                        className={`px-8 py-2.5 rounded-full text-white font-bold transition-all uppercase text-xs transform active:scale-95 min-w-[200px] shadow-lg ${
                            esPago 
                                ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-100' 
                                : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
                        }`}
                    >
                        {isSubmitting ? 'Procesando transacción...' : 'Aplicar Movimiento'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default ModalCobranzaART;